import React, { useMemo } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { MenuItem } from '@mui/material';
import { TextField } from '@mui/material';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { LORA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { EditRubricCard } from 'apps/sigma2-ui/src/components/rubrics/EditRubricCard';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';

export const EditRubricGeneralInfoSection: React.FC<{
  gradingType: RubricGradingType;
  name?: string;
  description?: string;
  integrationDataSetId?: string;
  onChangeName: (value: string) => void;
  onChangeDescription: (value: string) => void;
  onChangeIntegrationDataSetId: (value: string) => void;
  possibleIntegrationDataSets: DataSet[];
  isReadOnlyMode: boolean;
}> = ({
  gradingType,
  name,
  description,
  integrationDataSetId,
  onChangeName,
  onChangeDescription,
  onChangeIntegrationDataSetId,
  possibleIntegrationDataSets,
  isReadOnlyMode,
}) => {
  const language = useLanguage();

  const cardIconSrc = useMemo(() => {
    switch (gradingType) {
      case 'resume':
        return '/images/rubrics/rubric-resume-general-info-section-icon.png';
      case 'questions':
        return '/images/rubrics/rubric-question-general-info-section-icon.png';
      case 'interview':
        return '/images/rubrics/rubric-interview-general-info-section-icon.png';
      default:
        throw guardExhaustive(gradingType);
    }
  }, [gradingType]);

  return (
    <EditRubricCard>
      <Spacing size="lg" direction="horizontal">
        <Spacing size="lg" direction="vertical" className="p-6">
          <TextField
            variant="standard"
            size="medium"
            required
            value={name}
            onChange={(e) => onChangeName(e.currentTarget.value)}
            label={language.editRubricGeneralInfoSectionNameLabel()}
            placeholder={language.editRubricGeneralInfoSectionNamePlaceholder()}
            className="w-[521px]"
            disabled={isReadOnlyMode}
            slotProps={{
              root: {
                sx: {
                  backgroundColor: '#fff',
                },
              },
              htmlInput: {
                sx: {
                  fontSize: 40,
                  fontFamily: LORA_FONT_FAMILY,
                },
              },
            }}
          />
          <TextField
            variant="outlined"
            size="small"
            multiline
            rows={2}
            value={description}
            onChange={(e) => onChangeDescription(e.currentTarget.value)}
            label={language.editRubricGeneralInfoSectionDescriptionLabel()}
            placeholder={language.editRubricGeneralInfoSectionDescriptionPlaceholder()}
            disabled={isReadOnlyMode}
          />
          <TextField
            variant="outlined"
            size="medium"
            required
            select
            value={integrationDataSetId}
            onChange={(e) => onChangeIntegrationDataSetId(e.target.value)}
            label={language.editRubricGeneralInfoSectionIntegrationLabel()}
            helperText={language.editRubricGeneralInfoSectionIntegrationHelperText()}
            disabled={isReadOnlyMode}
            slotProps={{
              select: {
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) {
                    return (
                      <span style={{ color: '#292D33', fontSize: 16 }}>
                        {language.editRubricGeneralInfoSectionIntegrationPlaceholder()}
                      </span>
                    );
                  }

                  const found = possibleIntegrationDataSets.find(
                    (o) => o.id === selected,
                  );
                  return found?.displayName ?? '';
                },
              },
            }}
          >
            {possibleIntegrationDataSets.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.displayName}
              </MenuItem>
            ))}
          </TextField>
        </Spacing>
        <img src={cardIconSrc} width={255} />
      </Spacing>
    </EditRubricCard>
  );
};
